import {useState} from 'react';
import {
  TextConversationMessage,
  ConversationMessageStatus,
  Conversation,
} from '@stripe-internal/conversations-client';
import {FormattedMessage} from 'react-intl';
import {view, css, Link} from '@sail/ui/deprecated';
import {messages} from './messages';
import {TextRow} from '../../../Chat';
import {AvatarSpacer} from '../../../Chat/AvatarSpacer';
import {useMessageLinkIntercept} from '../../MessageLinkInterceptContext';
import {useTheme} from '../../../ThemeProvider';
import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
} from '../../../FeatureFlagsProvider';

type Props = {
  conversation: Conversation;
  message: TextConversationMessage;
  children?: React.ReactNode;
  avatar?: React.ReactNode | null | undefined;
  TextRowComponent?: React.ComponentType<React.ComponentProps<typeof TextRow>>;
};

export const TextMessageRow = ({
  conversation,
  message,
  avatar,
  children,
  TextRowComponent = TextRow,
}: Props) => {
  const useNewDesign = useNewUniversalHelpDesign();
  const useDotComDesign = useUniversalHelpDotComDesign();
  const theme = useTheme();
  const onMessageLinkClick = useMessageLinkIntercept();
  const [showOriginalMessage, setShowOriginalMessage] = useState(false);

  // Message text is rendered as sanitized markdown HTML, so anchors inside it
  // are not React elements. Capture clicks on the row to let the app intercept
  // links (e.g. threads switching to another conversation) before navigation.
  const handleClickCapture = (e: React.MouseEvent) => {
    if (!onMessageLinkClick) {
      return;
    }

    const anchor = (e.target as HTMLElement).closest?.('a');
    const href = anchor?.getAttribute('href');
    if (href && onMessageLinkClick(href)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const background =
    message.participant.id === conversation.user.id
      ? 'neutral600'
      : 'neutral100';

  const isOwnMessage = message.participant.id === conversation.user.id;
  const isBot = message.participant.role === 'bot';
  const position = isOwnMessage ? 'end' : 'start';

  const showMessageClickHandler = () => {
    setShowOriginalMessage(!showOriginalMessage);
  };

  const hasOriginalMessage =
    message.status !== ConversationMessageStatus.Error &&
    message.originalText &&
    message.originalText !== message.text &&
    !isOwnMessage;

  let messageStyle = message.style;
  if (useNewDesign) {
    // The new designs remove the bubble for bot answers. We still
    // render the `info` style if it's used
    messageStyle = message.style !== 'info' && isBot ? 'none' : message.style;
  }

  return (
    <view.div
      className="uh-conversations-text-message-row"
      data-testid="text-message-row"
      data-message-type={message.participant.role}
      onClickCapture={handleClickCapture}
    >
      <TextRowComponent
        isOwnMessage={isOwnMessage}
        messageId={message.id}
        useDotComDesign={useDotComDesign}
        body={
          showOriginalMessage && message.originalText
            ? message.originalText
            : message.text
        }
        messageType={message.participant.role}
        avatar={avatar}
        position={position}
        background={background}
        error={
          message.status === ConversationMessageStatus.Error ? (
            <FormattedMessage {...messages.sendError} />
          ) : null
        }
        style={messageStyle}
      >
        {children}
      </TextRowComponent>
      {hasOriginalMessage ? (
        <view.div
          css={{
            paddingTop: 'small',
            paddingBottom: 'small',
            stack: 'x',
            justifyContent:
              position === 'end'
                ? css.value('flex-end')
                : css.value('flex-start'),
            gap: 'small',
          }}
        >
          {!useNewDesign && avatar ? <AvatarSpacer /> : null}
          <Link
            type="primary"
            onPress={showMessageClickHandler}
            css={{
              color: css.value(theme.linkColor),
              '&:focus': {
                outline: theme.focusVisibleOutline,
                focusRing: theme.focusVisibleOutline
                  ? css.value('none')
                  : undefined,
              },
            }}
          >
            {showOriginalMessage ? (
              <FormattedMessage {...messages.translateCta} />
            ) : (
              <FormattedMessage {...messages.viewOriginalCta} />
            )}
          </Link>
        </view.div>
      ) : null}
    </view.div>
  );
};
